import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Source, SourceListResponse, SourceStatusResponse } from '@/types'
import {
  fetchSources,
  fetchSourceStatus,
  updateSource,
} from '@/api/client'
import { usePagination } from '@/composables/usePagination'

type ConnectionStatus = 'ok' | 'degraded' | 'lost'

export const useSourcesStore = defineStore('sources', () => {
  // ── State ──────────────────────────────────────────────────────────
  const sources = ref<Source[]>([])
  const total = ref(0)
  const loading = ref(false)
  const connectionStatus = ref<ConnectionStatus>('ok')
  const failedPolls = ref(0)

  // Pagination
  const { page, pageSize, hasMore, offset, setHasMore } = usePagination()

  // Current filters — preserved across poll refreshes
  const currentFilterType = ref<string | undefined>(undefined)
  const currentFilterActive = ref<boolean | undefined>(undefined)

  // Category names for the posts filter (hardcoded, not from API)
  const sourceNames = ref<string[]>([
    'abuse',
    'conditions',
    'medical',
    'payment',
    'equipment',
    'command',
    'mobilization',
    'rights',
    'mental_health',
    'unclear',
  ])

  // Polling
  let pollTimer: ReturnType<typeof setInterval> | null = null
  const POLL_INTERVAL = 30_000

  // ── Getters ────────────────────────────────────────────────────────
  const activeSources = computed(() => sources.value.filter((s) => s.is_active))
  const scraping = computed(() => sources.value.some((s) => s.status === 'scraping'))
  const hasErrors = computed(() => sources.value.some((s) => s.status === 'error'))

  // ── Actions ────────────────────────────────────────────────────────

  async function loadSources(
    source_type?: string,
    is_active?: boolean,
    _limit = pageSize.value,
    _offset = offset.value,
  ) {
    // Persist filters so poll refreshes don't lose them
    currentFilterType.value = source_type
    currentFilterActive.value = is_active

    loading.value = true
    try {
      const resp: SourceListResponse = await fetchSources(source_type, is_active, _limit, _offset)
      // Auto-navigate from empty page (e.g., after deletion or filter change)
      if (resp.items.length === 0 && page.value > 0) {
        page.value = 0
        await loadSources(source_type, is_active, pageSize.value, 0)
        return
      }
      sources.value = resp.items
      total.value = resp.total
      setHasMore(resp.total)
      // Reset connection status on success
      connectionStatus.value = 'ok'
      failedPolls.value = 0
    } catch (e) {
      failedPolls.value++
      if (failedPolls.value >= 3) {
        connectionStatus.value = 'lost'
      } else {
        connectionStatus.value = 'degraded'
      }
    } finally {
      loading.value = false
    }
  }

  async function toggleSource(sourceId: number, is_active: boolean) {
    const idx = sources.value.findIndex((s) => s.id === sourceId)
    if (idx === -1) return

    const original = sources.value[idx].is_active
    // Optimistic update
    sources.value[idx].is_active = is_active

    try {
      await updateSource(sourceId, { is_active })
    } catch (e) {
      // Revert on failure
      sources.value[idx].is_active = original
      throw e
    }
  }

  async function editSource(
    sourceId: number,
    changes: Pick<Source, 'display_name' | 'is_active'>,
  ) {
    const updated = await updateSource(sourceId, changes)
    const idx = sources.value.findIndex((source) => source.id === sourceId)
    if (idx !== -1) {
      sources.value[idx] = updated
    }
    return updated
  }

  async function pollStatus() {
    try {
      const status: SourceStatusResponse = await fetchSourceStatus()
      // Merge status into sources (lightweight refresh)
      // Full refresh only if scraping state changed
      const wasScraping = scraping.value
      const isScraping = status.scraping
      if (wasScraping !== isScraping) {
        // Use current filters AND current page so they don't reset
        await loadSources(
          currentFilterType.value,
          currentFilterActive.value,
          pageSize.value,
          offset.value,
        )
      }
      connectionStatus.value = 'ok'
      failedPolls.value = 0
    } catch (e) {
      failedPolls.value++
      if (failedPolls.value >= 3) {
        connectionStatus.value = 'lost'
      } else {
        connectionStatus.value = 'degraded'
      }
    }
  }

  function startPolling() {
    if (pollTimer) return
    pollTimer = setInterval(() => {
      if (document.visibilityState === 'hidden') return
      pollStatus()
    }, POLL_INTERVAL)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function nextPage() {
    if (!hasMore.value) return
    page.value++
    loadSources(currentFilterType.value, currentFilterActive.value)
  }

  function prevPage() {
    if (page.value <= 0) return
    page.value--
    loadSources(currentFilterType.value, currentFilterActive.value)
  }

  function resetPage() {
    page.value = 0
  }

  return {
    sources,
    total,
    loading,
    connectionStatus,
    sourceNames,
    activeSources,
    scraping,
    hasErrors,
    page,
    pageSize,
    hasMore,
    currentFilterType,
    currentFilterActive,
    loadSources,
    toggleSource,
    editSource,
    nextPage,
    prevPage,
    resetPage,
    startPolling,
    stopPolling,
  }
})
